import { DeleteOutlined, PlusOutlined, SettingOutlined } from '@ant-design/icons';
import { Button, message, Modal, Space, Table, Tag } from 'antd';
import { BasePageComponent } from 'components';
import React, { useCallback, useEffect, useState } from 'react';
import ShortsRenderPresetFormModal from '../components/ShortsRenderPresetFormModal';
import {
  RENDER_PRESET_COLOR_LABELS,
  RENDER_PRESET_CROP_POSITION_LABELS,
  RENDER_PRESET_CROP_RATIO_LABELS,
  RENDER_PRESET_EMPTY_DISPLAY,
  RENDER_PRESET_FIT_OUTPUT_SIZE,
  RENDER_PRESET_FONT_LABELS,
  RENDER_PRESET_OUTPUT_SIZES,
  RENDER_PRESET_TABLE_PAGE_SIZE,
} from '../constants';
import {
  formatPresetErrorMessage,
  shortsRenderPresetService,
} from '../services/shortsRenderPresetService';
import { ShortsRenderPresetInterface } from '../types/shortsRenderPresetType';

const renderOptional = (value: string | number | null | undefined): string =>
  value === null || value === undefined || value === ''
    ? RENDER_PRESET_EMPTY_DISPLAY
    : String(value);

const outputSizeOf = (preset: ShortsRenderPresetInterface): string =>
  preset.fit_mode === 'crop' && preset.crop_ratio
    ? RENDER_PRESET_OUTPUT_SIZES[preset.crop_ratio]
    : RENDER_PRESET_FIT_OUTPUT_SIZE;

const ShortsRenderPreset: React.FC = () => {
  const [presets, setPresets] = useState<ShortsRenderPresetInterface[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(RENDER_PRESET_TABLE_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPreset, setEditingPreset] = useState<ShortsRenderPresetInterface | null>(null);

  const fetchPresets = useCallback(async () => {
    setLoading(true);
    try {
      const response = await shortsRenderPresetService.fetchPresets(
        new URLSearchParams({ page: String(page), page_size: String(pageSize) })
      );
      setPresets(response.results ?? []);
      setTotal(response.count ?? 0);
    } catch (error) {
      message.error(formatPresetErrorMessage(error, 'Failed to load render presets'));
      setPresets([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchPresets();
  }, [fetchPresets]);

  const handleAdd = () => {
    setEditingPreset(null);
    setModalOpen(true);
  };

  const handleEdit = (preset: ShortsRenderPresetInterface) => {
    setEditingPreset(preset);
    setModalOpen(true);
  };

  const handleSaved = () => {
    setModalOpen(false);
    setEditingPreset(null);
    fetchPresets();
  };

  const handleDelete = (preset: ShortsRenderPresetInterface) => {
    Modal.confirm({
      title: `Delete "${preset.name}"?`,
      // The FK is SET_NULL: seasons keep working, they just fall back to server defaults.
      content:
        'Seasons currently using this render preset will be unlinked. Their shorts will be ' +
        'rendered with the server defaults from the next conversion on. The seasons and ' +
        'their content are not deleted.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await shortsRenderPresetService.deletePreset(preset.id);
          message.success('Render preset deleted');
          // Stepping back a page avoids landing on an empty last page.
          if (presets.length === 1 && page > 1) {
            setPage(page - 1);
          } else {
            fetchPresets();
          }
        } catch (error) {
          message.error(formatPresetErrorMessage(error, 'Failed to delete render preset'));
        }
      },
    });
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: '5%' },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    {
      title: 'Font',
      dataIndex: 'font',
      key: 'font',
      render: (value: ShortsRenderPresetInterface['font']) =>
        RENDER_PRESET_FONT_LABELS[value] ?? value,
    },
    {
      title: 'Colors',
      key: 'colors',
      render: (_: unknown, record: ShortsRenderPresetInterface) =>
        `${RENDER_PRESET_COLOR_LABELS[record.font_color] ?? record.font_color} on ` +
        `${RENDER_PRESET_COLOR_LABELS[record.bg_color] ?? record.bg_color}`,
    },
    {
      title: 'Sizes (ep/date/prog/sub)',
      key: 'sizes',
      render: (_: unknown, record: ShortsRenderPresetInterface) =>
        `${record.size_episode}/${record.size_date}/${record.size_program}/${record.size_subtitle}`,
    },
    {
      title: 'Fit',
      key: 'fit',
      render: (_: unknown, record: ShortsRenderPresetInterface) =>
        record.fit_mode === 'crop'
          ? `Crop · ${
              record.crop_ratio
                ? RENDER_PRESET_CROP_RATIO_LABELS[record.crop_ratio]
                : RENDER_PRESET_EMPTY_DISPLAY
            } · ${RENDER_PRESET_CROP_POSITION_LABELS[record.crop_position] ?? record.crop_position}`
          : 'Fit',
    },
    {
      title: 'Output',
      key: 'output',
      render: (_: unknown, record: ShortsRenderPresetInterface) => outputSizeOf(record),
    },
    {
      title: 'Labels',
      key: 'labels',
      render: (_: unknown, record: ShortsRenderPresetInterface) => {
        const on = [
          record.show_episode_name && 'Episode',
          record.show_on_air_date && 'Date',
          record.show_program_name && 'Program',
        ].filter(Boolean) as string[];
        return on.length === 0 ? (
          <span style={{ color: '#888' }}>None</span>
        ) : (
          <Space size={4} wrap>
            {on.map(label => (
              <Tag key={label}>{label}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Margins (T/B)',
      key: 'margins',
      render: (_: unknown, record: ShortsRenderPresetInterface) =>
        `${record.top_margin}/${record.bottom_margin}`,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (value: string) => renderOptional(value),
    },
    {
      title: 'ACTIONS',
      key: 'actions',
      width: '10%',
      render: (_: unknown, record: ShortsRenderPresetInterface) => (
        <Space>
          <Button
            icon={<SettingOutlined />}
            onClick={() => handleEdit(record)}
            className="action-btn"
          />
          <Button
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
            className="action-btn delete-btn"
          />
        </Space>
      ),
    },
  ];

  return (
    <BasePageComponent>
      <div style={{ padding: '24px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <div>
            <h1 style={{ margin: 0 }}>Shorts Render Preset</h1>
            <span style={{ color: '#888', fontSize: '13px' }}>
              Drawing style used when ODK renders a short itself. Separate from Shorts Preset, which
              is the work order sent to an outside vendor.
            </span>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            New Render Preset
          </Button>
        </div>

        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={presets}
          scroll={{ x: 'max-content' }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage);
              setPageSize(nextPageSize);
            },
          }}
        />

        <ShortsRenderPresetFormModal
          open={modalOpen}
          preset={editingPreset}
          onClose={() => {
            setModalOpen(false);
            setEditingPreset(null);
          }}
          onSaved={handleSaved}
        />
      </div>
    </BasePageComponent>
  );
};

export default ShortsRenderPreset;
